import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuItem {
  readonly label: string;
  readonly icon?: ReactNode;
  /** Rote „Gefahr"-Aktion (z. B. Löschen). */
  readonly danger?: boolean;
  readonly disabled?: boolean;
  /** Trennlinie ÜBER diesem Eintrag (um Gruppen zu trennen). */
  readonly separatorBefore?: boolean;
  readonly onSelect: () => void;
}

interface MenuState {
  readonly x: number;
  readonly y: number;
  readonly items: readonly ContextMenuItem[];
}

/**
 * FORQ-eigenes Rechtsklick-Menü statt des Browser-Menüs -- für das ERP-Gefühl. Ein Aufrufer bindet
 * `open` an sein `onContextMenu` und rendert `menu` irgendwo im Baum:
 *
 *   const { open, menu } = useContextMenu();
 *   <tr onContextMenu={(e) => open(e, [{ label: 'Öffnen', onSelect: () => … }])}>…</tr>
 *   {menu}
 *
 * Das Menü hängt per Portal am <body> (keine Clipping-/Transform-Probleme), klemmt sich an den
 * Viewport-Rand und schließt bei Klick daneben, Escape, Scrollen oder Fensterwechsel.
 */
export function useContextMenu() {
  const [state, setState] = useState<MenuState | null>(null);

  const open = useCallback((event: MouseEvent, items: readonly ContextMenuItem[]) => {
    if (items.length === 0) return;
    // Browser-Kontextmenü unterdrücken und unser eigenes an den Cursor setzen.
    event.preventDefault();
    event.stopPropagation();
    setState({ x: event.clientX, y: event.clientY, items });
  }, []);

  /** Wie open(), aber ohne Event -- für globale Listener (native Rechtsklick-Behandlung). */
  const openAt = useCallback((x: number, y: number, items: readonly ContextMenuItem[]) => {
    if (items.length === 0) return;
    setState({ x, y, items });
  }, []);

  const close = useCallback(() => setState(null), []);

  const menu = state
    ? createPortal(<ContextMenuSurface x={state.x} y={state.y} items={state.items} onClose={close} />, document.body)
    : null;

  return { open, openAt, close, menu };
}

function ContextMenuSurface({ x, y, items, onClose }: {
  readonly x: number;
  readonly y: number;
  readonly items: readonly ContextMenuItem[];
  readonly onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  // Nach dem Layout an den Viewport-Rand klemmen, damit das Menü nicht abgeschnitten wird.
  //
  // WICHTIG: <html> trägt `zoom: 1.25`. Unter CSS-zoom sind clientX/clientY, getBoundingClientRect
  // und innerWidth/Height SICHTBARE Pixel, die CSS-Eigenschaft left/top wird aber im gezoomten Raum
  // interpretiert (sichtbar = Wert × zoom). Ohne Korrektur landet das Menü bei clientX × 1.25 --
  // also weit rechts/unter dem Cursor. Deshalb in sichtbaren Pixeln klemmen und den finalen Wert
  // durch zoom teilen.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
    const rect = element.getBoundingClientRect();
    const visualLeft = Math.max(8, Math.min(x, window.innerWidth - rect.width - 8));
    const visualTop = Math.max(8, Math.min(y, window.innerHeight - rect.height - 8));
    setPos({ left: visualLeft / zoom, top: visualTop / zoom });
  }, [x, y]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    const onScroll = () => onClose();
    document.addEventListener('mousedown', onClose);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    window.addEventListener('blur', onClose);
    return () => {
      document.removeEventListener('mousedown', onClose);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="forq-context-menu"
      role="menu"
      style={{ left: pos.left, top: pos.top }}
      onMouseDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map((item, index) => (
        <div key={index} className="forq-context-menu__slot">
          {item.separatorBefore && index > 0 ? <div className="forq-context-menu__sep" /> : null}
          <button
            type="button"
            role="menuitem"
            className={`forq-context-menu__item${item.danger ? ' is-danger' : ''}`}
            disabled={item.disabled}
            onClick={() => { onClose(); item.onSelect(); }}
          >
            {item.icon ? <span className="forq-context-menu__icon">{item.icon}</span> : null}
            <span>{item.label}</span>
          </button>
        </div>
      ))}
    </div>
  );
}
